import {
  Autocomplete,
  Box,
  Checkbox,
  TextField,
  Typography,
  BoxProps,
  SelectProps,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { MAX_MULTISELECT_OPTIONS } from '@common/types/constants';

type NestedOption = { value: string; label: string };

interface IDynamicFormFieldProps {
  name: string;
  label?: string;
  disabled?: boolean;
  optional?: boolean;
  placeholder?: string;
  handleChange: (values: string[]) => void;
  value: string[];
  options?: NestedOption[];
  clearable?: boolean;
  error?: boolean;
  minOptionMenuWidth?: string;
  disableAllOption?: boolean;
  none_options?: string[];
}

export const MultiselectField = ({
  name,
  label,
  options = [],
  disabled = false,
  optional,
  handleChange,
  value,
  clearable = true,
  error,
  placeholder = 'Type here...',
  sx,
  disableAllOption = false,
  minOptionMenuWidth,
  none_options = [],
  ...props
}: IDynamicFormFieldProps & Pick<BoxProps, 'sx'> & SelectProps) => {
  const finalOptions =
    clearable && !disableAllOption
      ? [{ value: '', label: 'All' }, ...options]
      : options;

  const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
  const checkedIcon = <CheckBoxIcon fontSize='small' />;

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <Autocomplete<NestedOption, true, true, false>
        id={name}
        getOptionDisabled={option => {
          if (option.value === '') return false;

          const alreadySelected = value.includes(option.value);
          return value.length >= MAX_MULTISELECT_OPTIONS && !alreadySelected;
        }}
        multiple
        disableCloseOnSelect
        filterSelectedOptions={false}
        options={finalOptions}
        getOptionLabel={o => o.label}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        value={finalOptions.filter(o => value.includes(o.value))}
        size={props.size}
        slotProps={{
          ...(minOptionMenuWidth && {
            paper: {
              style: {
                minWidth: minOptionMenuWidth,
              },
            },
          }),
        }}
        onChange={(_, newValue) => {
          if (newValue.some(o => o.value === '')) {
            handleChange([]);
            return;
          }

          const newValues = newValue.map(o => o.value);

          if (none_options.length > 0) {
            const lastAdded = newValues.find(v => !value.includes(v));
            if (lastAdded && none_options.includes(lastAdded)) {
              // Selecting a none option clears everything else
              handleChange([lastAdded]);
              return;
            }
            // Selecting a regular option clears any none options
            handleChange(newValues.filter(v => !none_options.includes(v)));
            return;
          }

          handleChange(newValues);
        }}
        renderOption={({ ...props }, option, { selected }) => {
          const isAll = option.value === '';
          const isAllChecked = isAll && value.length === 0;

          return (
            <li {...{ ...props, key: props.id }}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                checked={isAll ? isAllChecked : selected}
                sx={{ mr: 1 }}
              />
              <Typography variant='body2'>{option.label}</Typography>
            </li>
          );
        }}
        renderInput={params => {
          const { slotProps: _sp, ...restParams } = params;
          const selectedLabels = finalOptions
            .filter(o => value.includes(o.value))
            .map(o => o.label)
            .join(', ');

          return (
            <TextField
              {...restParams}
              label={label}
              required={!optional}
              error={error}
              // placeholder={
              //   value.length === 0 && !disableAllOption ? 'All' : ''
              // }
              helperText={
                value.length >= MAX_MULTISELECT_OPTIONS
                  ? `Maximum of ${MAX_MULTISELECT_OPTIONS} items selected.`
                  : undefined
              }
              slotProps={{
                input: {
                  ...params.slotProps.input,
                  inputProps: {
                    ...params.slotProps.htmlInput,
                    value: selectedLabels
                      ? selectedLabels
                      : disableAllOption
                        ? placeholder
                        : 'All',
                    readOnly: true,
                    style: {
                      position:
                        selectedLabels && !props.startAdornment
                          ? 'absolute'
                          : 'static',
                      left: 0,
                      bottom: 0,
                      backgroundColor: 'transparent',
                      zIndex:
                        selectedLabels && !props.startAdornment
                          ? -1
                          : 'initial',
                    },
                  },
                  ...(props.startAdornment && {
                    startAdornment: props.startAdornment,
                  }),
                },
              }}
            />
          );
        }}
        disabled={disabled}
      />
    </Box>
  );
};
